import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

const TITLE = "Perimenopause Test: At-Home Kits & Quiz";
const DESCRIPTION =
  "Guide to perimenopause testing: FSH blood tests, at-home hormone kits, when symptoms alone suffice, plus a free self-assessment quiz.";
const URL = "https://knowmeno.com/guide/perimenopause-test";

export const Route = createFileRoute("/guide/perimenopause-test")({
  head: () => ({
    meta: [
      { title: `${TITLE} — KnowMeno` },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          image: ["https://knowmeno.com/og-image.png?v=2"],
          datePublished: "2026-01-15",
          dateModified: "2026-06-29",
          inLanguage: "en",
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "KnowMeno" },
          publisher: {
            "@type": "Organization",
            name: "KnowMeno",
            logo: { "@type": "ImageObject", url: "https://knowmeno.com/icon-512.png" },
          },
          mainEntityOfPage: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Is there a reliable test for perimenopause?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "No single lab test reliably confirms perimenopause. FSH, estradiol and AMH can support the picture, but hormones fluctuate dramatically cycle-to-cycle. For women over 45 with typical symptoms, most menopause societies recommend diagnosing from symptoms and cycle changes alone.",
              },
            },
            {
              "@type": "Question",
              name: "Do at-home perimenopause test kits work?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "At-home FSH urine and finger-prick blood kits can detect elevated FSH, but a single result is a snapshot and easily misleading during perimenopause. They are best used as a conversation starter with a clinician, not a diagnosis.",
              },
            },
            {
              "@type": "Question",
              name: "When should I actually get a perimenopause blood test?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Testing is most useful if you are under 45 with menopausal symptoms (to investigate early menopause / POI), if your symptoms are atypical, or if hormonal contraception is masking your cycle pattern.",
              },
            },
            {
              "@type": "Question",
              name: "Can I be in perimenopause with normal hormone levels?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. A normal FSH or estradiol does not rule out perimenopause. Symptoms and cycle changes are usually more informative than a single lab value.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: PerimenopauseTestGuide,
});

const PLUM = "#4B1F4F";
const PINK = "#FF5C7A";
const CREAM = "#FAF7F5";
const BORDER = "#E8D5DA";
const TEXT = "#2D1A2E";
const MUTED = "#6E4B71";

interface TestRow {
  name: string;
  what: string;
  useful: string;
  limits: string;
}

const TESTS: TestRow[] = [
  {
    name: "FSH (blood)",
    what: "Follicle-stimulating hormone; rises as ovarian reserve declines.",
    useful: "Helpful if you're under 45 with menopausal symptoms, or have no cycle to track.",
    limits: "Swings wildly cycle-to-cycle in perimenopause. One reading can be misleading.",
  },
  {
    name: "Estradiol (E2)",
    what: "The main ovarian estrogen.",
    useful: "Paired with FSH for context, or to monitor HRT dose.",
    limits: "Also highly variable. A normal value does not rule out perimenopause.",
  },
  {
    name: "AMH",
    what: "Anti-Müllerian hormone; reflects remaining egg supply.",
    useful: "Better at predicting time-to-menopause and fertility window than FSH.",
    limits: "Doesn't tell you what symptoms you'll feel or when they'll start.",
  },
  {
    name: "TSH / thyroid panel",
    what: "Thyroid hormones.",
    useful: "Rules out thyroid disease, which mimics perimenopause (fatigue, mood, weight, periods).",
    limits: "Not a perimenopause test — a differential diagnosis.",
  },
  {
    name: "At-home FSH urine kit",
    what: "Dipstick that detects elevated urinary FSH.",
    useful: "Cheap, private, can prompt a clinician visit.",
    limits: "Single snapshot, no estradiol context, no clinical interpretation.",
  },
  {
    name: "At-home finger-prick panel",
    what: "Mail-in blood sample for FSH, estradiol, sometimes AMH.",
    useful: "More data points than a urine strip; results reviewed by a lab.",
    limits: "Still a single timepoint. Pricey. Easy to over-interpret without a clinician.",
  },
];

interface Question {
  id: string;
  text: string;
}

const QUESTIONS: Question[] = [
  { id: "cycle", text: "My periods have become irregular, heavier, lighter, or skipped." },
  { id: "hotflash", text: "I get hot flashes or night sweats." },
  { id: "sleep", text: "My sleep has gotten noticeably worse in the past 1–2 years." },
  { id: "mood", text: "My mood, anxiety, or irritability feels different from how I usually am." },
  { id: "brainfog", text: "I notice brain fog, memory slips, or trouble concentrating." },
  { id: "joint", text: "I have new joint aches, stiffness, or muscle pain." },
  { id: "libido", text: "My libido or vaginal comfort has changed." },
  { id: "energy", text: "My energy is lower than it used to be without an obvious cause." },
];

function PerimenopauseTestGuide() {
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [age, setAge] = useState<string>("");
  const [submitted, setSubmitted] = useState(false);

  const score = useMemo(() => Object.values(answers).filter(Boolean).length, [answers]);
  const ageNum = parseInt(age, 10);

  const result = useMemo(() => {
    if (!submitted) return null;
    const isUnder45 = !Number.isNaN(ageNum) && ageNum < 45;
    if (score >= 5 && !isUnder45) {
      return {
        title: "Symptoms strongly consistent with perimenopause",
        body: "Your symptom pattern is what major menopause societies (NAMS, BMS) consider sufficient to diagnose perimenopause in women over 45 — no blood test required. A lab panel is unlikely to change the diagnosis. The most useful next step is tracking symptoms over time so your clinician sees the trend, not just one snapshot.",
        suggestTest: false,
      };
    }
    if (score >= 5 && isUnder45) {
      return {
        title: "Symptoms consistent with perimenopause — testing is worth discussing",
        body: "Because you're under 45, a clinician will usually want to confirm with bloods (FSH on two occasions 4–6 weeks apart, plus estradiol and thyroid panel) to rule out early menopause or other causes. Bring symptom tracking with you.",
        suggestTest: true,
      };
    }
    if (score >= 3) {
      return {
        title: "Possible early perimenopause",
        body: "You have a meaningful cluster of symptoms but not the full picture. Track for 8–12 weeks to see if a pattern emerges, and consider a clinician visit. Testing may be useful if your cycle is masked by hormonal contraception or if you're under 45.",
        suggestTest: isUnder45,
      };
    }
    return {
      title: "Symptoms are mild or non-specific right now",
      body: "What you're describing could be perimenopause, stress, sleep debt, thyroid, or several other things. A blood test in isolation is unlikely to be informative. Track for a few cycles and revisit — and see a clinician sooner if anything is severe or changing fast.",
      suggestTest: false,
    };
  }, [submitted, score, ageNum]);

  return (
    <div
      style={{
        background: CREAM,
        minHeight: "100vh",
        fontFamily: "system-ui, -apple-system, sans-serif",
        color: TEXT,
        padding: "32px 16px 80px",
      }}
    >
      <article
        style={{
          maxWidth: 720,
          margin: "0 auto",
          background: "#FFFFFF",
          borderRadius: 20,
          padding: "32px 24px",
          border: `0.5px solid ${BORDER}`,
        }}
      >
        <nav style={{ fontSize: 12, color: MUTED, marginBottom: 16 }}>
          <Link to="/" style={{ color: MUTED, textDecoration: "none" }}>
            KnowMeno
          </Link>{" "}
          / Guides / Perimenopause Test
        </nav>

        <h1
          style={{
            fontSize: 32,
            lineHeight: 1.15,
            color: PLUM,
            margin: "0 0 12px",
            fontWeight: 500,
            letterSpacing: "-0.02em",
          }}
        >
          Perimenopause Test Guide & Self-Assessment
        </h1>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: MUTED, marginTop: 0 }}>
          What kinds of perimenopause tests exist, when a test actually helps, when your symptoms
          are enough on their own — plus a free 8-question self-assessment quiz you can use to
          frame a smarter doctor conversation.
        </p>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            Is there a single "perimenopause test"?
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            Short answer: no. Perimenopause is a clinical diagnosis built from your symptom
            pattern, your age, and your cycle history. Hormones in this stage are erratic by
            definition — they swing dramatically from week to week — so any single blood draw is
            a snapshot of a moving target. For women over 45 with typical symptoms,
            <strong> NAMS and the British Menopause Society explicitly recommend against</strong>{" "}
            routine hormone testing.
          </p>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 12 }}>
            Types of perimenopause tests
          </h2>
          <div
            style={{
              border: `1px solid ${BORDER}`,
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: PLUM, color: "#FFFFFF", textAlign: "left" }}>
                  <th style={{ padding: "10px 12px", fontWeight: 500 }}>Test</th>
                  <th style={{ padding: "10px 12px", fontWeight: 500 }}>What it measures</th>
                  <th style={{ padding: "10px 12px", fontWeight: 500 }}>When it helps</th>
                  <th style={{ padding: "10px 12px", fontWeight: 500 }}>Limits</th>
                </tr>
              </thead>
              <tbody>
                {TESTS.map((t) => (
                  <tr key={t.name} style={{ borderTop: `1px solid ${BORDER}` }}>
                    <td style={{ padding: "12px", fontWeight: 500, verticalAlign: "top" }}>
                      {t.name}
                    </td>
                    <td style={{ padding: "12px", color: MUTED, verticalAlign: "top", lineHeight: 1.55 }}>
                      {t.what}
                    </td>
                    <td style={{ padding: "12px", color: MUTED, verticalAlign: "top", lineHeight: 1.55 }}>
                      {t.useful}
                    </td>
                    <td style={{ padding: "12px", color: MUTED, verticalAlign: "top", lineHeight: 1.55 }}>
                      {t.limits}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            When a test is genuinely useful
          </h2>
          <ul style={{ fontSize: 15, lineHeight: 1.8, paddingLeft: 20 }}>
            <li>You are <strong>under 45</strong> with menopausal symptoms (to investigate early menopause / POI).</li>
            <li>Your symptoms are <strong>atypical</strong> or could plausibly be thyroid, anemia, or depression.</li>
            <li>You have <strong>no cycle to track</strong> — hysterectomy, hormonal IUD, continuous pill.</li>
            <li>You're starting or adjusting <strong>HRT</strong> and your clinician wants a baseline.</li>
          </ul>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            When your symptoms are enough
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            If you're over 45, still cycling (even irregularly), and experiencing the familiar
            cluster — vasomotor symptoms, sleep disruption, mood changes, brain fog, joint
            aches — a hormone panel rarely changes the plan. What changes the plan is{" "}
            <strong>weeks of consistent tracking</strong> that show your clinician the pattern.
          </p>
        </section>

        <section
          style={{
            marginTop: 32,
            background: CREAM,
            border: `1px solid ${BORDER}`,
            borderRadius: 16,
            padding: "24px 20px",
          }}
        >
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginTop: 0, marginBottom: 6 }}>
            Free perimenopause self-assessment quiz
          </h2>
          <p style={{ fontSize: 13, color: MUTED, marginTop: 0, marginBottom: 16, lineHeight: 1.55 }}>
            Tick anything that applies in the last 1–2 years. This is an educational tool, not a
            diagnosis.
          </p>

          <label style={{ display: "block", fontSize: 14, color: TEXT, marginBottom: 16 }}>
            Your age (optional)
            <input
              type="number"
              inputMode="numeric"
              min={18}
              max={75}
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder="e.g. 47"
              style={{
                display: "block",
                marginTop: 6,
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${BORDER}`,
                fontSize: 14,
                width: 120,
                background: "#FFFFFF",
                color: TEXT,
              }}
            />
          </label>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {QUESTIONS.map((q) => {
              const checked = !!answers[q.id];
              return (
                <label
                  key={q.id}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 12px",
                    background: "#FFFFFF",
                    border: `1px solid ${BORDER}`,
                    borderRadius: 10,
                    fontSize: 14,
                    lineHeight: 1.5,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      setAnswers((prev) => ({ ...prev, [q.id]: e.target.checked }))
                    }
                    style={{ marginTop: 3, accentColor: PLUM }}
                  />
                  <span>{q.text}</span>
                </label>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => setSubmitted(true)}
            style={{
              marginTop: 18,
              background: PLUM,
              color: "#FFFFFF",
              border: "none",
              padding: "12px 18px",
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            See my result
          </button>

          {submitted && result ? (
            <div
              style={{
                marginTop: 20,
                padding: "16px 18px",
                background: "#FFFFFF",
                border: `1px solid ${BORDER}`,
                borderRadius: 12,
              }}
            >
              <div style={{ fontSize: 13, color: MUTED, marginBottom: 4 }}>
                Score: {score} of {QUESTIONS.length}
              </div>
              <h3 style={{ fontSize: 17, color: PLUM, margin: "0 0 8px", fontWeight: 500 }}>
                {result.title}
              </h3>
              <p style={{ fontSize: 14, color: TEXT, lineHeight: 1.65, margin: 0 }}>
                {result.body}
              </p>
              {result.suggestTest ? (
                <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, marginTop: 10 }}>
                  Ask your clinician about an FSH + estradiol panel (ideally repeated 4–6 weeks
                  apart) and a TSH to rule out thyroid causes.
                </p>
              ) : null}
            </div>
          ) : null}
        </section>

        <section style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            Turn this into a data-driven doctor conversation
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            KnowMeno takes 30 seconds a day and builds a clinical-grade Symptom Intelligence
            Report you can hand to your GP or menopause specialist. Instead of "I think I'm in
            perimenopause," you walk in with weeks of cycle, symptom, sleep, and mood trends —
            and any lab values you already have — in one place.
          </p>
          <div style={{ marginTop: 18, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link
              to="/sign-up"
              style={{
                background: PLUM,
                color: "#FFFFFF",
                padding: "12px 18px",
                borderRadius: 12,
                textDecoration: "none",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Start tracking free for 7 days
            </Link>
            <Link
              to="/guide/fsh-levels"
              style={{
                background: "transparent",
                color: PLUM,
                padding: "12px 18px",
                borderRadius: 12,
                textDecoration: "none",
                fontSize: 14,
                fontWeight: 500,
                border: `1px solid ${PLUM}`,
              }}
            >
              Read: FSH levels chart
            </Link>
          </div>
        </section>

        <section style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 12 }}>
            Frequently asked questions
          </h2>

          <details style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 0" }}>
            <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: 15, color: PLUM }}>
              Is there a reliable test for perimenopause?
            </summary>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 8 }}>
              No single lab reliably confirms perimenopause. Hormones fluctuate too much. For
              women over 45 with typical symptoms, diagnosis is clinical — based on symptoms and
              cycle changes.
            </p>
          </details>

          <details style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 0" }}>
            <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: 15, color: PLUM }}>
              Do at-home perimenopause test kits work?
            </summary>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 8 }}>
              They can flag elevated FSH but a single snapshot is easily misleading in
              perimenopause. Treat them as a conversation starter with a clinician, not a
              diagnosis.
            </p>
          </details>

          <details style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 0" }}>
            <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: 15, color: PLUM }}>
              When should I actually get a perimenopause blood test?
            </summary>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 8 }}>
              Most useful if you're under 45 with symptoms, if symptoms are atypical, or if
              hormonal contraception is masking your cycle pattern.
            </p>
          </details>

          <details
            style={{
              borderTop: `1px solid ${BORDER}`,
              borderBottom: `1px solid ${BORDER}`,
              padding: "12px 0",
            }}
          >
            <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: 15, color: PLUM }}>
              Can I be in perimenopause with normal hormone levels?
            </summary>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 8 }}>
              Yes. A normal FSH or estradiol does not rule out perimenopause. Symptoms and cycle
              changes are usually more informative than a single lab value.
            </p>
          </details>
        </section>

        <p
          style={{
            marginTop: 28,
            fontSize: 12,
            color: MUTED,
            lineHeight: 1.6,
            fontStyle: "italic",
            borderTop: `1px solid ${BORDER}`,
            paddingTop: 16,
          }}
        >
          KnowMeno is a wellness and tracking tool, not a medical device. This guide and the
          quiz are for general education and do not provide medical advice, diagnosis, or
          treatment. Always discuss results and decisions with a qualified clinician who knows
          your full history.
        </p>

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <Link
            to="/"
            style={{ color: PINK, fontSize: 14, textDecoration: "none", fontWeight: 500 }}
          >
            ← Back to KnowMeno
          </Link>
        </div>
      </article>
    </div>
  );
}
